import { Pagination, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { getRecentTransactions } from '../Redux/Slice/dashboard.slice';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaChevronDown, FaChevronUp, FaShoppingBag, FaReceipt } from 'react-icons/fa';

export default function Transaction() {
    const [searchValue, setSearchValue] = useState('');
    const dispatch = useDispatch();
    const transactions = useSelector((state) => state.dashboard.recentTransactions);

    useEffect(() => {
        dispatch(getRecentTransactions());
    }, [dispatch])

    const [expandedRows, setExpandedRows] = useState({});

    const handleToggleExpand = (transactionId) => {
        setExpandedRows((prev) => ({
            ...prev,
            [transactionId]: !prev[transactionId],
        }));
    };

    const filteredData = transactions.filter(data => (
        data?.fullName?.toLowerCase().includes(searchValue.toLowerCase()) ||
        data?.userEmail?.toLowerCase().includes(searchValue.toLowerCase()) ||
        data?.items?.some(item => item.gameTitle?.toLowerCase().includes(searchValue.toLowerCase())) ||
        data?.status?.toLowerCase().includes(searchValue.toLowerCase()) ||
        data?.totalAmount.toString().toLowerCase().includes(searchValue.toLowerCase())
    ));

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const currentItems = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white tracking-tight">Order Management</h1>
                    <p className="text-gray-400">Monitor and track all customer transactions.</p>
                </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm">
                <div className="p-6 border-b border-white/5 flex flex-col sm:flex-row gap-4 items-center justify-between">
                    <div className="relative w-full sm:w-80 group">
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-primary transition-colors" />
                        <input
                            type="text"
                            placeholder="Search orders, customers..."
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-2.5 text-sm text-white focus:border-primary/50 outline-none transition-all"
                        />
                    </div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                        Total Transactions: {filteredData.length}
                    </p>
                </div>

                <div className="overflow-x-auto no-scrollbar">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-white/[0.02]">
                                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Order Details</th>
                                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Customer</th>
                                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Purchased Content</th>
                                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Amount</th>
                                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {currentItems.map((order, index) => (
                                <tr key={index} className="hover:bg-white/[0.02] transition-colors group">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                                                <FaReceipt />
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-xs font-bold text-white uppercase tracking-wider">#{order.orderId.slice(-8)}</span>
                                                <span className="text-[10px] text-gray-500 font-bold uppercase">{order.duration}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-bold text-white">{order?.fullName}</span>
                                            <span className="text-xs text-gray-400">{order?.userEmail}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 min-w-[300px]">
                                        <div className="space-y-3">
                                            {(expandedRows[order.orderId] ? order.items : order.items.slice(0, 1)).map((item, idx) => (
                                                <motion.div key={idx} initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-3">
                                                    {item.gameImage ? (
                                                        <img src={item.gameImage} alt="" className="w-8 h-8 rounded-lg object-cover border border-white/10 shadow-lg" />
                                                    ) : (
                                                        <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-primary font-bold border border-white/5">
                                                            <FaShoppingBag size={12} />
                                                        </div>
                                                    )}
                                                    <div className="flex flex-col">
                                                        <span className="text-xs font-bold text-white">{item.gameTitle}</span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[9px] text-gray-500 uppercase font-black">{item.platform}</span>
                                                            <span className="text-[9px] text-primary font-bold">${item.price}</span>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            ))}
                                            {order.items.length > 1 && (
                                                <button onClick={() => handleToggleExpand(order.orderId)} className="flex items-center gap-1.5 text-[10px] font-black uppercase text-gray-500 hover:text-primary transition-colors tracking-widest pl-11">
                                                    {expandedRows[order.orderId] ? <><FaChevronUp size={8} /> Less Content</> : <><FaChevronDown size={8} /> +{order.items.length - 1} More Items</>}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className="text-sm font-bold text-white shadow-primary/20">${order.totalAmount}</span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider ${order.status === 'paid' ? 'bg-green-500/10 text-green-400 border border-green-500/20 shadow-[0_0_15px_rgba(34,197,94,0.1)]' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'}`}>
                                            {order.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="p-6 border-t border-white/5 flex justify-end">
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(_, page) => setCurrentPage(page)}
                            sx={{
                                "& .MuiPaginationItem-root": { color: "#9ca3af", borderColor: "rgba(255,255,255,0.1)", borderRadius: "12px" },
                                "& .Mui-selected": { backgroundColor: "rgba(98,99,237,0.2) !important", color: "#6263ED !important", borderColor: "rgba(98,99,237,0.5) !important" }
                            }}
                        />
                    </div>
                )}
            </div>
        </motion.div>
    )
}